import { RouteComponentProps } from "@reach/router"
import {
  ApiError,
  Box,
  DocumentTitleOverride,
  FullPageLoader,
  Grid,
  GridCell,
  Link,
  StatusBar,
} from "@shared/primitive-components"

import { useState } from "react"
import { useFindTrip } from "../../../../api/insights"
import { ScreenContentWithContext } from "../../../../components/ScreenContentWithContext"
import { usePolicyAndFleet } from "../../../../contexts"
import { TripCard, TripDetailsCard, TripMap, TripMapFromString } from "../../../components/SafetyInsightsComponents"
import { tripDetailsStyles as styles } from "../../../styles"
import { isNewPipelinePolicy } from "../../../../utils"

type Props = RouteComponentProps<{
  tripId: string
}>

export const TRIP_DTI = {
  TRIP_SCORE: `trip-score`,
  TRIP_DETAILS: `trip-details`,
  TRIP_INCORRECT_SEGMENT_BANNER: `trip-incorrect-segment-banner`,
  TRIP_INCORRECT_SEGMENT_COPY: `trip-incorrect-segment-copy`,
  TRIP_INCORRECT_SEGMENT_FAQ_LINK: `trip-incorrect-segment-faq-link`,
}

export const NotificationCopy = (
  <Box css={styles.notificationCopy} data-testid={TRIP_DTI.TRIP_INCORRECT_SEGMENT_COPY}>
    Please note that the grey segments of this trip contain&nbsp;
    <Link
      data-testid={TRIP_DTI.TRIP_INCORRECT_SEGMENT_FAQ_LINK}
      target="_blank"
      css={styles.notificationCopy}
      href="https://help.flockcover.com/why-might-my-data-be-unreliable"
    >
      unreliable data.
    </Link>
  </Box>
)

export const Trip = ({ tripId }: Props) => {
  const [hasUnreliableTripData, setHasUnreliableTripData] = useState(false)
  const { policy } = usePolicyAndFleet()
  const useNewPipeline = isNewPipelinePolicy(policy.id)
  const [res] = useFindTrip({
    policyId: policy.id,
    tripId: tripId ?? ``,
  })

  if (res.result?.tag === `err`) return <ApiError error={res.result.error.type} />
  if (res.loading || !res.result) return <FullPageLoader />

  const trip = res.result.data

  return (
    <>
      <DocumentTitleOverride>Flock Motor | Safety Score - Trip</DocumentTitleOverride>
      <ScreenContentWithContext>
        <Grid css={styles.fullWidth}>
          <GridCell span={12} data-testid={TRIP_DTI.TRIP_SCORE}>
            <TripCard trip={trip} />
          </GridCell>
          {hasUnreliableTripData && (
            <GridCell span={12}>
              <StatusBar
                type="important"
                placement="middle"
                message={NotificationCopy}
                dataTestId={TRIP_DTI.TRIP_INCORRECT_SEGMENT_BANNER}
              />
            </GridCell>
          )}
          <GridCell css={styles.mapContainer} tablet={8} desktop={8}>
            {useNewPipeline ? (
              <TripMapFromString
                geojsonString={trip.geoJsonStringContent!}
                setHasUnreliableTripData={setHasUnreliableTripData}
              />
            ) : (
              <TripMap geojsonUrl={trip.geojsonUrl} setHasUnreliableTripData={setHasUnreliableTripData} />
            )}
          </GridCell>
          <GridCell tablet={8} desktop={4}>
            <TripDetailsCard trip={trip} data-testid={TRIP_DTI.TRIP_DETAILS} />
          </GridCell>
        </Grid>
      </ScreenContentWithContext>
    </>
  )
}
